import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { validate } from "capitalrx-components-2.0";
import {
  Checkbox,
  CheckboxValue,
  DateSerializer,
  Datebox,
  Dropdown,
  InvalidValue,
  Textbox,
  useSimpleInputState,
} from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";
import inflection from "inflection";
import { DateTime } from "luxon";

import { Drug, DrugConfiguration } from "@/api/types";
import { useSelectedEnrollmentPlan } from "@/premium/hooks";
import { EnrollmentPlan, getPlanAPI } from "@/api/axios";

export interface DrugDetailsFormProps {
  readonly drug: Drug;
}

export const DrugDetailsForm: FC<DrugDetailsFormProps> = ({ drug }) => {
  const { t: translate } = useTranslation();
  const drugConfigForm = useFormContext();
  const [isCustomQuantity, setIsCustomQuantity] = useSimpleInputState<CheckboxValue>(false);
  const [isCustomDaysSupply, setIsCustomDaysSupply] = useSimpleInputState<CheckboxValue>(false);
  const drugForm = drugConfigForm.watch("drug.drugForm");
  const drugDosage = drugConfigForm.watch("drug.drugDosage");
  const { enrollmentPlanCsid } = useSelectedEnrollmentPlan();
  const [isLoadingPlan, setIsLoadingPlan] = useState<boolean>(true);
  const [plan, setPlan] = useState<EnrollmentPlan>(null);

  useEffect(() => {
    if (enrollmentPlanCsid) {
      setIsLoadingPlan(true);
      getPlanAPI(enrollmentPlanCsid.id)
        .then((plan) => {
          setPlan(plan);
        })
        .finally(() => {
          setIsLoadingPlan(false);
        });
    }
  }, [enrollmentPlanCsid]);

  const { colSpan } = useResponsive({
    colSpan: {
      mobile: 12,
      tablet: 6,
      desktop: 3,
    },
  });

  // when the form changes, pick the 0th dosage as the default
  useEffect(() => {
    drugConfigForm.setValue("drug.drugDosage", drugDosageList[0]);
    setIsCustomQuantity(false, true);
  }, [drugForm]);

  // when the dosage or form changes, pick the 0th quantity as the default
  useEffect(() => {
    drugConfigForm.setValue("drug.drugQuantity", drugQuantitiesList[0]);
    setIsCustomQuantity(false, true);
  }, [drugDosage, drugForm]);

  const drugFormList = useMemo(() => {
    if (drug) {
      const drugFormList = drug.configurations.reduce((accum: Array<string>, configuration: DrugConfiguration) => {
        if (!accum.includes(configuration.dosage_form)) {
          accum.push(configuration.dosage_form);
        }

        return accum;
      }, []);

      return drugFormList.sort();
    } else {
      return [];
    }
  }, [drug]);

  const drugDosageList = useMemo(() => {
    if (drug && drugForm) {
      const dosageList = drug.configurations.reduce((accum: Array<string>, configuration: DrugConfiguration) => {
        if (configuration.dosage_form === drugForm && !accum.includes(configuration.dosage)) {
          accum.push(configuration.dosage);
        }

        return accum;
      }, []);

      return dosageList.sort((currentDosage, nextDosage) => {
        return currentDosage.localeCompare(nextDosage, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });
    } else {
      return [];
    }
  }, [drug, drugForm]);

  const drugQuantitiesList = useMemo(() => {
    if (drug) {
      let newDrugQuantityList: Array<string> = [];

      const drugConfiguration = drug.configurations.find((configuration) => {
        return configuration.dosage_form === drugForm && configuration.dosage === drugDosage;
      });

      if (drugConfiguration) {
        const dosageFormPackaging = drugConfiguration.dosage_form_packaging || drugConfiguration.dosage_form;
        newDrugQuantityList = drugConfiguration.display_quantities.map((display_quantity) => {
          return inflection.inflect(`${display_quantity} ${dosageFormPackaging}`, +display_quantity);
        });
      }

      return newDrugQuantityList;
    } else {
      return [];
    }
  }, [drug, drugForm, drugDosage]);

  const validateCustomQuantity = useCallback((value: string) => {
    const isNumeric = validate.isNumeric(value, translate("FIND_PHARMACIES.drugDetailsForm.invalidNumber"));
    if (isNumeric === true) {
      if (+value < 1 || +value > 9999) {
        return translate("FIND_PHARMACIES.drugDetailsForm.quantityMustBeBetween");
      }
    } else {
      return isNumeric;
    }
    return true;
  }, []);

  // get packaging form for selected drug and drug form
  const getDrugDosagePackaging = useCallback(
    (drugForm: string): string => {
      const drugDosagePackaging = drug?.configurations?.find((options) => {
        if (options.dosage_form === drugForm) {
          return options;
        }
      });

      if (!drugDosagePackaging?.dosage_form_packaging) {
        return drugDosagePackaging?.dosage_form;
      }

      return drugDosagePackaging?.dosage_form_packaging;
    },
    [drug?.configurations],
  );

  // get default dosage because most_common_dosage isn't always the full text
  const defaultDosage = useCallback((): string => {
    const commonDosage = drug?.most_common_dosage;

    const defaultDosage = drug?.configurations?.find((options) => {
      if (options.dosage_form === drug?.most_common_form && options.dosage.includes(commonDosage)) {
        return options;
      }
    });

    return defaultDosage?.dosage;
  }, [drug?.configurations, drug?.most_common_dosage, drug?.most_common_form]);

  // calculate default displayed quantity
  const defaultDisplayQuantity = useCallback((): number => {
    if (drug?.most_common_quantity_dispensed) {
      const commonQuantity = parseInt("" + drug?.most_common_quantity_dispensed);

      const drugQuantitiesMultiplier = drug?.configurations?.find((options) => {
        if (options.dosage_form === drug?.most_common_form && options.dosage.includes(drug?.most_common_dosage)) {
          return options;
        }
      });

      return Math.round(commonQuantity / drugQuantitiesMultiplier?.quantity_multiplier);
    }

    return parseInt(drug?.configurations[0]?.display_quantities[0]);
  }, [drug?.most_common_quantity_dispensed, drug?.configurations, drug?.most_common_form, drug?.most_common_dosage]);

  // when the form changes and the most common form is selected, choose the most common dosage and quantity
  const [didSetDefaults, setDidSetDefaults] = useState(false);
  useEffect(() => {
    if (!didSetDefaults && drug && drug?.most_common_form && drug?.most_common_form == drugForm && drugQuantitiesList.length > 0) {
      // set default values
      const drugDefaultDosage = defaultDosage();
      const drugDefaultForm = drug?.most_common_form;

      const commonQuantity = defaultDisplayQuantity();
      let drugDefaultQuantity: string;
      if (commonQuantity) {
        const dosagePackagingVariable = getDrugDosagePackaging(drugDefaultForm);
        drugDefaultQuantity = inflection.inflect(`${commonQuantity} ${dosagePackagingVariable}`, +commonQuantity);
        if (!drugQuantitiesList.includes(drugDefaultQuantity)) {
          drugDefaultQuantity = "" + commonQuantity;
          setIsCustomQuantity(true, false);
        }
      } else {
        drugDefaultQuantity = drugQuantitiesList[0];
      }

      drugConfigForm.setValue("drug.drugForm", drugDefaultForm);
      drugConfigForm.setValue("drug.drugDosage", drugDefaultDosage);
      drugConfigForm.setValue("drug.drugQuantity", drugDefaultQuantity);
      setDidSetDefaults(true);
    }
  }, [
    drug,
    didSetDefaults,
    drugForm,
    drugFormList,
    drugDosageList,
    drugQuantitiesList,
    defaultDosage,
    defaultDisplayQuantity,
    getDrugDosagePackaging,
  ]);

  const validateDaysSupply = useCallback((value: string) => {
    const isNumeric = validate.isNumeric(value, translate("FIND_PHARMACIES.drugDetailsForm.invalidNumber"));
    if (isNumeric) {
      return isNumeric;
    }

    const numericValue = +value;
    if (numericValue > 999) {
      return translate("FIND_PHARMACIES.drugDetailsForm.daysSupplyCannotBeGreater");
    }
    if (numericValue < 1) {
      return translate("FIND_PHARMACIES.drugDetailsForm.daysSupplyCannotBeLess");
    }

    return true;
  }, []);

  const validateClaimRunDate = useCallback(
    (value: string | InvalidValue) => {
      if (enrollmentPlanCsid && value) {
        if (typeof value === "string") {
          const enrollmentPlanRunDateLocal = DateTime.fromISO(enrollmentPlanCsid.claim_run_date).toLocal();
          const enrollmentPlanRunDateRounded = DateTime.fromObject({
            year: enrollmentPlanRunDateLocal.year,
            month: enrollmentPlanRunDateLocal.month,
            day: enrollmentPlanRunDateLocal.day,
          });

          const formattedEnrollmentPlanRunDate = enrollmentPlanRunDateRounded.toFormat("MM/dd/yyyy");
          const passedInValueLocal = DateTime.fromFormat(value, "MM/dd/yyyy");

          if (passedInValueLocal < enrollmentPlanRunDateRounded) {
            return `${translate("FIND_PHARMACIES.drugDetailsForm.dateCannotBeBefore")} ${formattedEnrollmentPlanRunDate}.`;
          }

          const dateDiff = passedInValueLocal.diff(enrollmentPlanRunDateRounded, "years");
          if (dateDiff.years > 1) {
            return `${translate("FIND_PHARMACIES.drugDetailsForm.dateCannotBeMore")} ${formattedEnrollmentPlanRunDate}`;
          }
        } else if (value.type === "invalid") {
          return translate("FIND_PHARMACIES.drugDetailsForm.providedDateIsInvalid");
        }
      }
      return undefined;
    },
    [enrollmentPlanCsid],
  );

  const validateAge = useCallback((value: string) => {
    const isNumeric = validate.isNumeric(value);
    if (isNumeric) {
      return isNumeric;
    }
    const numericValue = +value;
    if (numericValue < 1) {
      return translate("FIND_PHARMACIES.drugDetailsForm.ageMustBeGreater");
    }
    if (numericValue > 150) {
      return translate("FIND_PHARMACIES.drugDetailsForm.ageMustBeLess");
    }
    return undefined;
  }, []);

  const handleCustomQuantityChange = (newValue: CheckboxValue, prev: CheckboxValue) => {
    if (!newValue) {
      drugConfigForm.setValue("drug.drugQuantity", drugQuantitiesList[0]);
    } else {
      drugConfigForm.setValue("drug.drugQuantity", "");
    }
    setIsCustomQuantity(newValue, prev);
  };

  const handleDaysSupplyChange = (newValue: CheckboxValue, prev: CheckboxValue) => {
    if (!newValue) {
      drugConfigForm.setValue("drug.daysSupply", "30");
    } else {
      drugConfigForm.setValue("drug.daysSupply", "");
    }
    setIsCustomDaysSupply(newValue, prev);
  };

  return (
    <Stack spacing="large">
      <Grid>
        <Grid.Span columns={colSpan}>
          {!isCustomQuantity && (
            <Dropdown.Field required name="drug.drugQuantity" label={translate("FIND_PHARMACIES.drugDetailsForm.quantity")}>
              {drugQuantitiesList.map((quantity) => {
                return (
                  <Dropdown.Option key={quantity} value={quantity}>
                    {quantity}
                  </Dropdown.Option>
                );
              })}
            </Dropdown.Field>
          )}
          {isCustomQuantity && (
            <Textbox.Field
              testId="custom-quantity"
              name="drug.drugQuantity"
              label={translate("FIND_PHARMACIES.drugDetailsForm.quantity")}
              instructions={translate("FIND_PHARMACIES.drugDetailsForm.customQuantityRange")}
              required
              validator={validateCustomQuantity}
            />
          )}
        </Grid.Span>
        <Grid.Span columns={colSpan}>
          <Dropdown.Field
            name="drug.drugDosage"
            label={translate("FIND_PHARMACIES.drugDetailsForm.dosage")}
            placeholder={translate("FIND_PHARMACIES.drugDetailsForm.select")}
            required
          >
            {drugDosageList.map((dosage) => {
              return (
                <Dropdown.Option key={dosage} value={dosage}>
                  {dosage}
                </Dropdown.Option>
              );
            })}
          </Dropdown.Field>
        </Grid.Span>
        <Grid.Span columns={colSpan}>
          <Dropdown.Field
            name="drug.drugForm"
            label={translate("FIND_PHARMACIES.drugDetailsForm.form")}
            placeholder={translate("FIND_PHARMACIES.drugDetailsForm.select")}
            required
          >
            {drugFormList.map((df) => {
              return (
                <Dropdown.Option key={df} value={df}>
                  {df}
                </Dropdown.Option>
              );
            })}
          </Dropdown.Field>
        </Grid.Span>
        <Grid.Break />
        <Grid.Span columns={colSpan}>
          <Checkbox
            value={isCustomQuantity}
            onChange={handleCustomQuantityChange}
            label={translate("FIND_PHARMACIES.drugDetailsForm.customQuantity")}
            hideLabel
          />
        </Grid.Span>
      </Grid>
      <Grid>
        <Body>
          {translate("FIND_PHARMACIES.drugDetailsForm.enterMoreInformation")} ({translate("FIND_PHARMACIES.drugDetailsForm.optional")})
        </Body>
        <Grid.Break />
        <Grid.Span columns={colSpan}>
          {!isCustomDaysSupply && (
            <Dropdown.Field
              validator={validateDaysSupply}
              name="drug.daysSupply"
              label={translate("FIND_PHARMACIES.drugDetailsForm.daysSupply")}
              required
            >
              <Dropdown.Option key="30" value="30">
                30
              </Dropdown.Option>
              <Dropdown.Option key="90" value="90">
                90
              </Dropdown.Option>
            </Dropdown.Field>
          )}
          {isCustomDaysSupply && (
            <Textbox.Field
              testId="custom-days-supply"
              name="drug.daysSupply"
              label={translate("FIND_PHARMACIES.drugDetailsForm.daysSupply")}
              instructions={translate("FIND_PHARMACIES.drugDetailsForm.customDaysSupplyRange")}
              validator={validateDaysSupply}
              required
            />
          )}
        </Grid.Span>
        <Grid.Span columns={colSpan}>
          <Datebox.Field
            zone="local"
            serializer={DateSerializer.Literal}
            label={translate("FIND_PHARMACIES.drugDetailsForm.date")}
            instructions={translate("FIND_PHARMACIES.drugDetailsForm.enterTheDateYouPlan")}
            name="drug.claimRunDate"
            validator={validateClaimRunDate}
            required
          />
        </Grid.Span>
        <Grid.Break />
        <Grid.Span columns={colSpan}>
          <Checkbox
            value={isCustomDaysSupply}
            onChange={handleDaysSupplyChange}
            label={translate("FIND_PHARMACIES.drugDetailsForm.customDaysSupply")}
            hideLabel
          />
        </Grid.Span>
        <Grid.Break />
        {!isLoadingPlan && plan.plan.client_id !== "JD339" as string && (
          <Grid.Span columns={colSpan}>
            <Dropdown.Field label={translate("FIND_PHARMACIES.drugDetailsForm.gender")} name="member.gender">
              <Dropdown.Option value="Female">{translate("FIND_PHARMACIES.drugDetailsForm.female")}</Dropdown.Option>
              <Dropdown.Option value="Male">{translate("FIND_PHARMACIES.drugDetailsForm.male")}</Dropdown.Option>
              <Dropdown.Option value="Other">{translate("FIND_PHARMACIES.drugDetailsForm.other")}</Dropdown.Option>
              <Dropdown.Option value="X">{translate("FIND_PHARMACIES.drugDetailsForm.x")}</Dropdown.Option>
            </Dropdown.Field>
          </Grid.Span>
        )}
        <Grid.Span columns={colSpan}>
          <Textbox.Field name="member.age" label={translate("FIND_PHARMACIES.drugDetailsForm.age")} validator={validateAge} />
        </Grid.Span>
      </Grid>
    </Stack>
  );
};
